import { deleteDB, openDB } from "idb";
function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) resolve(value);
    else Promise.resolve(value).then(_next, _throw);
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(void 0);
        });
    };
}
const dbName = 'file-cache';
class FileCache {
    init() {
        return _async_to_generator(function*() {
            if (this.db) return this.db;
            if (!this.initPromise) this.initPromise = this.openDatabase().finally(()=>{
                this.initPromise = null;
            });
            return this.initPromise;
        }).call(this);
    }
    openDatabase() {
        return _async_to_generator(function*() {
            this.db = yield openDB('file-values', 1, {
                upgrade (db) {
                    if (!db.objectStoreNames.contains(dbName)) db.createObjectStore(dbName);
                }
            });
            this.db.onclose = ()=>{
                this.db = null;
            };
            this.db.onversionchange = ()=>{
                var _this_db;
                null == (_this_db = this.db) || _this_db.close();
                this.db = null;
            };
            return this.db;
        }).call(this);
    }
    resizeImage(blob, maxWidth, maxHeight) {
        return new Promise((resolve, reject)=>{
            const img = new Image();
            const url = URL.createObjectURL(blob);
            img.onload = function() {
                URL.revokeObjectURL(url);
                let newWidth = img.width;
                let newHeight = img.height;
                if (img.width > maxWidth || img.height > maxHeight) {
                    const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
                    newWidth = Math.floor(img.width * ratio);
                    newHeight = Math.floor(img.height * ratio);
                }
                const canvas = document.createElement('canvas');
                canvas.width = newWidth;
                canvas.height = newHeight;
                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, newWidth, newHeight);
                canvas.toBlob((resizedBlob)=>{
                    if (resizedBlob) resolve(resizedBlob);
                    else reject(new Error('Failed to create resized blob'));
                }, 'image/jpeg', 0.8);
            };
            img.onerror = ()=>{
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load image'));
            };
            img.src = url;
        });
    }
    blobToBase64(blob) {
        return _async_to_generator(function*() {
            return new Promise((resolve, reject)=>{
                const reader = new FileReader();
                reader.onload = ()=>{
                    try {
                        const dataUrl = reader.result;
                        const base64 = dataUrl.split(',')[1];
                        if (!base64) throw new Error('not a base64 url');
                        resolve(dataUrl);
                    } catch (e) {
                        reject(e);
                    }
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        })();
    }
    base64ToBlob(base64) {
        try {
            const parts = base64.split(',');
            const sliceSize = 512;
            const header = parts[0] || '';
            const base64Data = parts[1] || '';
            const mimeMatch = header.match(/:(.*?);/);
            if (!mimeMatch) throw new Error('Could not extract MIME type from data URL');
            const mimeType = mimeMatch[1];
            const byteCharacters = atob(base64Data);
            const byteArrays = [];
            for(let offset = 0; offset < byteCharacters.length; offset += sliceSize){
                const slice = byteCharacters.slice(offset, offset + sliceSize);
                const byteNumbers = new Array(slice.length);
                for(let i = 0; i < slice.length; i++)byteNumbers[i] = slice.charCodeAt(i);
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new Blob(byteArrays, {
                type: mimeType
            });
        } catch (error) {
            throw new Error(`Failed to convert base64 to blob: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    save(blob) {
        return _async_to_generator(function*() {
            try {
                var _window_crypto;
                const id = (null == (_window_crypto = window.crypto) ? void 0 : _window_crypto.randomUUID) ? window.crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c)=>('x' === c ? 16 * Math.random() | 0 : 4 * Math.random() | 8).toString(16));
                const db = yield this.init();
                if (!db) return null;
                const base64 = yield this.blobToBase64(blob);
                let tx = db.transaction(dbName, 'readwrite');
                let store = tx.objectStore(dbName);
                yield store.put(base64, `${id}_full`);
                yield tx.done;
                let preview = blob;
                if (blob && blob.type.startsWith('image/')) preview = yield this.resizeImage(blob, 300, 600);
                const base64Preview = yield this.blobToBase64(preview);
                tx = db.transaction(dbName, 'readwrite');
                store = tx.objectStore(dbName);
                yield store.put(base64Preview, `${id}_preview`);
                yield tx.done;
                return id;
            } catch (error) {
                this.logger.error(error, {
                    tags: {
                        vkdoc_error_type: 'idb'
                    }
                });
                return null;
            }
        }).call(this);
    }
    get(id) {
        return _async_to_generator(function*() {
            try {
                const db = yield this.init();
                if (!db) return null;
                const tx = db.transaction(dbName, 'readonly');
                const store = tx.objectStore(dbName);
                const retrievedBlob = yield this.base64ToBlob((yield store.get(`${id}_full`)));
                yield tx.done;
                return retrievedBlob || null;
            } catch (error) {
                this.logger.error(error, {
                    tags: {
                        vkdoc_error_type: 'idb'
                    }
                });
                return null;
            }
        }).call(this);
    }
    getPreview(id) {
        return _async_to_generator(function*() {
            try {
                const db = yield this.init();
                if (!db) return null;
                const tx = db.transaction(dbName, 'readonly');
                const store = tx.objectStore(dbName);
                const preview = yield this.base64ToBlob((yield store.get(`${id}_preview`)));
                yield tx.done;
                return preview || null;
            } catch (error) {
                this.logger.error(error, {
                    tags: {
                        vkdoc_error_type: 'idb'
                    }
                });
                return null;
            }
        }).call(this);
    }
    deleteDB() {
        return _async_to_generator(function*() {
            try {
                yield deleteDB('file-values');
            } catch (e) {}
        })();
    }
    getImageResolution(file) {
        const image = new Promise((resolve, reject)=>{
            const reader = new FileReader();
            reader.onload = ()=>{
                const img = new Image();
                img.onload = ()=>{
                    resolve({
                        width: img.width,
                        height: img.height
                    });
                };
                img.onerror = reject;
                img.src = reader.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
        return image;
    }
    createFileMeta(blob, filename = '') {
        return _async_to_generator(function*() {
            const mimeType = yield this.fileTypeChecker.fileTypeFromBlob(blob);
            if (mimeType.startsWith('image/')) {
                let width = 0;
                let height = 0;
                try {
                    const resolution = yield this.getImageResolution(blob);
                    width = resolution.width;
                    height = resolution.height;
                } catch (e) {}
                return {
                    type: 'image',
                    mimeType,
                    size: blob.size,
                    localCacheId: '',
                    name: filename,
                    width,
                    height
                };
            }
            return {
                type: 'general',
                mimeType,
                size: blob.size,
                localCacheId: '',
                name: filename
            };
        }).call(this);
    }
    constructor(logger, fileTypeChecker){
        this.logger = logger;
        this.fileTypeChecker = fileTypeChecker;
        this.db = null;
        this.initPromise = null;
    }
}
export { FileCache };
